import type { Property } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useOutletContext, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Badge, Spinner } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { isAdmin, useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { STATUS_KEYS, STATUS_TONE } from "@/lib/labels";
import { cn } from "@/lib/utils";

export function usePropertyContext() {
  return useOutletContext<{ property: Property }>();
}

const TABS = [
  { to: "", key: "tab.info", end: true },
  { to: "structure", key: "tab.structure", end: false },
  { to: "panels", key: "tab.panels", end: false },
  { to: "media", key: "tab.media", end: false },
  { to: "qr", key: "tab.qr", end: false },
  { to: "owner", key: "tab.owner", end: false },
] as const;

export function PropertyDetailPage() {
  const { id = "" } = useParams();
  const { t } = useT();
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState(false);

  const query = useQuery({
    queryKey: ["property", id],
    queryFn: async () =>
      unwrap(await api.GET("/api/properties/{id}/", { params: { path: { id } } })),
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await api.DELETE("/api/properties/{id}/", { params: { path: { id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["properties"] });
      void qc.invalidateQueries({ queryKey: ["count"] });
      nav("/properties", { replace: true });
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  if (query.isLoading) {
    return (
      <div className="p-10 text-center">
        <Spinner className="size-8" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="p-10 text-center">
        <p className="mb-4 text-muted-foreground">{t("props.not_found")}</p>
        <Button asChild variant="outline">
          <Link to="/properties">{t("props.back")}</Link>
        </Button>
      </div>
    );
  }
  const p = query.data;

  return (
    <>
      <div className="mb-4">
        <Link
          to="/properties"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"
        >
          <ArrowLeft className="size-4" /> {t("props.back")}
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-xl font-bold md:text-2xl">
              {p.address} {p.house_number}
            </h1>
            <Badge tone={STATUS_TONE[p.status ?? "draft"]}>
              {t(STATUS_KEYS[p.status ?? "draft"])}
            </Badge>
          </div>
          {isAdmin(user) && (
            <Button variant="outline" size="sm" onClick={() => setConfirm(true)}>
              <Trash2 className="size-4" /> {t("common.delete")}
            </Button>
          )}
        </div>
      </div>

      <nav
        aria-label="property tabs"
        className="-mx-3 mb-4 flex overflow-x-auto border-b border-border px-3 md:mx-0 md:px-0"
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              cn(
                "min-h-11 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold",
                isActive
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )
            }
          >
            {t(tab.key)}
          </NavLink>
        ))}
      </nav>

      <Outlet context={{ property: p }} />

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={t("props.delete_title")}
        message={t("props.delete_message")}
        confirmLabel={t("common.delete")}
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </>
  );
}
